/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Default feature flags — used as base values, merged with server-side cockpitSettings during login.
 */
export const DEFAULT_FEATURE_FLAGS = {
  // STRING FEATURES
  logo: 'stage',
  theme: 'dark',
  defaultPortfolioView: 'scanner',
  defaultCollectionView: 'main',
  defaultMarketView: 'main',
  newsletterBanner: '',
  newsletterLogo: '',
  newsletterDisclaimer: '',
  newsletterEmail: '',
  // NUMBER FEATURES
  collectionsLimit: 15,
  queriesPerCollectionsLimit: 30,
  portfoliosLimit: 100,
  entitiesPerPortfoliosLimit: 10000,
  // BOOLEAN FEATURES
  activityLogging: true,
  annotationsEnabled: true,
  betaLanguages: false,
  flaggingEnabled: true,
  dismissingEnabled: true,
  copyingArticleLinkEnabled: true,
  bookmarkingEnabled: true,
  importExportBookmarks: false,
  reportingEnabled: true,
  gettingHighlightsEnabled: true,
  addQueries: true,
  editQueries: true,
  importQueries: true,
  exportQueries: true,
  linkQueries: true,
  importCollections: true,
  exportCollections: true,
  createCollections: true,
  addEntities: true,
  editEntities: true,
  importPortfolios: true,
  exportPortfolios: true,
  createPortfolios: true,
  showMenuLHS: true,
  showMenuRHS: true,
  showMenuTop: true,
  showEventAlerts: true,
  showSanctions: true,
  showFactTempFilter: true,
  showTopNews: false,
  showKeywordSearch: true,
  showDeltaAlerts: true,
  // top menu options
  showLogo: true,
  showNavigation: false,
  showSearchMenu: true,
  showFilter: true,
  showSearchBar: true,
  showSearchQuery: true,
  showTheme: true,
  showFullscreen: true,
  showHelp: true,
  // board options
  showHomepage: true,
  // news lab options
  showNewsLab: true,
  showPortfolio: true,
  showCollection: true,
  // LLM Chat
  showChatLLM: true,
  // invest lab options
  showInvestLab: true,
  showSignals: true,
  showTrending: true,
  // user lab options
  showUserLab: true,
  showNewsletter: true,
  showAlerts: true,
  showAlertsSetup: true,
  showFlagsSetup: true,
  showTutorial: true,
  showSettings: true,
  // menu account options
  showMenuAccount: true,
  showEmail: true,
  showFeedback: true,
  showTutorialFull: true,
  showAdmin: true,
  showStats: true,
  showPassword: true,
  showLogout: true,
  // expand button
  showExpand: true,
  // view options
  showViewOptions: true,
  showScannerView: true,
  showMainView: true,
  showChart: true,
  showNewsView: true,
  showParticipants: true,
  showBenchmark: true,
  showNewsletterPreview: true,
  showNewsletterLivePreview: true,
  showNewsletterArticles: true,
  // sort options
  showSortOptions: true,
  showSortSentiment: true,
  showSortAlphabetic: true,
  showSortTrending: true,
  showSortVolumeCount: true,
  showSortVolumeRatio: true,
};

/**
 * Default scorelab settings — used as base values, merged with server-side settings during login.
 */
export const DEFAULT_SCORELAB_SETTINGS = {
  sentimentAccess: true,
  generalRiskAccess: false,
  creditRiskAccess: false,
  esgRiskAccess: false,
  immediateRiskAccess: false,
  esgAccess: false,
  eb_simAccess: false,
  bbAccess: false,
  customScoreAccess: false,
  defaultScore: 'sentiment',
  customScores: [] as any[],
  defaultFlag: null as { score_type: string; [key: string]: any } | null,
  customFlags: [] as any[],
};

/**
 * Default admin scorelab settings — full access enabled.
 */
export const DEFAULT_ADMIN_SCORELAB_SETTINGS = {
  sentimentAccess: true,
  generalRiskAccess: true,
  creditRiskAccess: true,
  esgRiskAccess: true,
  immediateRiskAccess: true,
  esgAccess: true,
  eb_simAccess: false,
  bbAccess: true,
  customScoreAccess: true,
  defaultScore: 'sentiment',
  customScores: [] as any[],
  defaultFlag: null as { score_type: string; [key: string]: any } | null,
  customFlags: [] as any[],
};

/**
 * Centralized user state service.
 * Owns the shared BehaviorSubject-like state that was previously scattered across AuthService.
 * AuthService delegates to this service via getter/setter proxies.
 */
@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  /** Selected query (duplicate) */
  query: any;

  /** All queries */
  querys: any;

  /** Active query */
  iquery: any;

  /** User feature flags */
  featureFlags: any = Object.assign({}, DEFAULT_FEATURE_FLAGS);

  /** Scorelab settings */
  scorelabSettings: any = Object.assign({}, DEFAULT_SCORELAB_SETTINGS);

  /** Entities sent as array in request body */
  payloadEntities: any[] = [];

  /** Selected query for newsfeed */
  queryForNewsfeed: any;

  /** Active filter id (set by menu-filters; read by newsfeed/view). */
  filter: any;

  /** Custom default filter object (set by filters.guard from saved settings). */
  defaultFilter: any;

  /** Cached events list (populated once by menu-filters, reused by setup screens). */
  eventsList: any;

  scoreFocusedType: any = null;
  scoreFocusedColor = 'transparent';
  scoreFocusedTimeframe: any = null;
  scoreFocusedEvents: any = null;
  scoreFocusedEventsAnalytics: any = null;
  scoreFocusedTopEvents: any = null;

  private readonly _showWarningFlag$ = new BehaviorSubject<boolean>(false);
  readonly showWarningFlag$: Observable<boolean> =
    this._showWarningFlag$.asObservable();

  get showWarningFlag(): boolean {
    return this._showWarningFlag$.getValue();
  }
  set showWarningFlag(value: boolean) {
    this._showWarningFlag$.next(value);
  }

  private readonly _showCautionFlag$ = new BehaviorSubject<boolean>(false);
  readonly showCautionFlag$: Observable<boolean> =
    this._showCautionFlag$.asObservable();

  get showCautionFlag(): boolean {
    return this._showCautionFlag$.getValue();
  }
  set showCautionFlag(value: boolean) {
    this._showCautionFlag$.next(value);
  }

  /**
   * Reset all state to defaults (called on logout).
   */
  reset(): void {
    this.query = undefined;
    this.querys = undefined;
    this.iquery = undefined;
    this.featureFlags = Object.assign({}, DEFAULT_FEATURE_FLAGS);
    this.scorelabSettings = Object.assign({}, DEFAULT_SCORELAB_SETTINGS);
    this.payloadEntities = [];
    this.queryForNewsfeed = undefined;
    this.filter = undefined;
    this.defaultFilter = undefined;
    this.eventsList = undefined;
    this.scoreFocusedType = null;
    this.scoreFocusedColor = 'transparent';
    this.scoreFocusedTimeframe = null;
    this.scoreFocusedEvents = null;
    this.scoreFocusedEventsAnalytics = null;
    this.scoreFocusedTopEvents = null;
    this.showWarningFlag = false;
    this.showCautionFlag = false;
  }
}
